import React, { useState } from "react";
import { X } from "lucide-react";

/**
 * Compact chip-input for free-form lists (sizes, colours, tags).
 * - User types, presses Enter or comma to add
 * - X on each chip to remove
 * - Touch-friendly: chips are 32px tall (above WCAG 24px touch target min)
 *
 *   <ChipInput
 *     value={form.sizes}
 *     onChange={(v) => update("sizes", v)}
 *     placeholder="e.g. S, M, L, XL"
 *     data-testid="input-sizes"
 *   />
 */
const ChipInput = ({
  value = [],
  onChange,
  placeholder = "Type and press Enter",
  className = "",
  "data-testid": testId,
  maxChips = 24,
  chipClass = "bg-[#1A2024] text-white",
}) => {
  const [draft, setDraft] = useState("");

  const addChips = (raw) => {
    const parts = raw.split(/[,\n]/).map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const next = Array.from(new Set([...value, ...parts])).slice(0, maxChips);
    onChange(next);
    setDraft("");
  };

  const removeChip = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (draft.trim()) addChips(draft);
    } else if (e.key === "Backspace" && !draft && value.length > 0) {
      removeChip(value.length - 1);
    }
  };

  return (
    <div className={`flex flex-wrap gap-1.5 p-2 min-h-[44px] rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-[#C05A3C]/30 focus-within:border-[#C05A3C] ${className}`} data-testid={testId}>
      {value.map((chip, i) => (
        <span key={`${chip}-${i}`} className={`inline-flex items-center gap-1 px-2 h-7 rounded-md text-xs font-sans-ui ${chipClass}`} data-testid={`${testId}-chip-${i}`}>
          {chip}
          <button type="button" onClick={() => removeChip(i)} className="hover:opacity-70" aria-label={`Remove ${chip}`} data-testid={`${testId}-chip-remove-${i}`}>
            <X className="w-3 h-3"/>
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => draft.trim() && addChips(draft)}
        placeholder={value.length === 0 ? placeholder : ""}
        className="flex-1 min-w-[120px] bg-transparent text-sm outline-none px-1"
        data-testid={`${testId}-input`}
        style={{ touchAction: "manipulation" }}
      />
    </div>
  );
};

export default ChipInput;
